// Dono: B3 (organização). /colecoes (lista, criar manual/automática) e /colecoes/:id (cards, ordem, exclusões).
import { useEffect, useId, useRef, useState } from "preact/hooks";
import { ACCEPTED_MIME, type Card, type Space } from "../../../../shared/types.ts";
import { Caption, captionOf, Frame, Grid, GridIngest, ImageRetry } from "../Grid.tsx";
import { IconChevronDown, IconClose, IconPlus, IconPrev, IconUp } from "../Icons.tsx";
import { cardText, KIND_LABEL, urlFromText } from "../lib.ts";
import { QueryVersionNote } from "../Search.tsx";
import { backTo, density, failed, store, toast, up } from "../state.ts";
import { Confirm, Ring, ringOf, SpaceDialog, type SpaceDialogMode, spacePath, Thumb, useSpaceCards } from "../spaces/common.tsx";
import { cardsLabel, moveTo, nameTaken } from "../spaces/order.ts";
import { navTo } from "../spaces/selection.ts";

export function SpacesPage({ path }: { path: string }) {
  const id = path.startsWith("/colecoes/") ? decodeURIComponent(path.slice("/colecoes/".length)) : "";
  return id ? <SpaceView key={id} id={id} /> : <SpaceIndex />;
}

const SPACE_MIME = "application/x-faig-space";
const MOVE_SHORTCUTS = "Shift+ArrowLeft Shift+ArrowRight";
const tileLink = (id: string) => document.querySelector<HTMLElement>(`.space-tile[data-id="${CSS.escape(id)}"] .space-link`);

function SpaceIndex() {
  const [dialog, setDialog] = useState(false);
  const [said, setSaid] = useState("");
  const hint = useId();
  const spaces = store.spaces.value;
  const s = store.status.value;
  /** Leva a coleção ao índice `to` da lista: posição no meio dos vizinhos, gravada com updateSpace (vai para os outros aparelhos). */
  const move = (id: string, to: number) => {
    const order = spaces.map((x) => x.id);
    const p = moveTo(order, new Map(spaces.map((x) => [x.id, x.position])), id, to);
    if (p === null) return;
    store.updateSpace(id, { position: p }).catch(failed);
    setSaid(`Posição ${Math.max(0, Math.min(to, order.length - 1)) + 1} de ${order.length}.`);
    requestAnimationFrame(() => tileLink(id)?.focus());
  };
  /** Soltou `moving` antes/depois da coleção no índice `at`: o índice final conta que ela sai do lugar antigo. */
  const dropOn = (moving: string, at: number, after: boolean) => {
    const from = spaces.findIndex((x) => x.id === moving);
    const to = at + (after ? 1 : 0);
    move(moving, from >= 0 && from < to ? to - 1 : to);
  };
  return (
    <main id="main" tabIndex={-1} class="page organize spaces">
      <header class="page-head org-head">
        <h1>Coleções</h1>
        <button type="button" class="pill ring-pill new-space" aria-label="Nova coleção" onClick={() => setDialog(true)}>
          <IconPlus />
          <span aria-hidden="true">Nova coleção</span>
        </button>
      </header>
      {spaces.length ? (
        <>
          <ul class="space-grid">
            {spaces.map((sp, i) => (
              <SpaceTile key={sp.id} space={sp} index={i} hint={hint} onMove={move} onDropOn={dropOn} />
            ))}
          </ul>
          <p id={hint} class="sr-only">
            Arraste uma coleção ou use Shift + seta para mudar a ordem.
          </p>
          <p class="sr-only" aria-live="polite">
            {said}
          </p>
        </>
      ) : (
        (s.lastSyncAt !== null || !s.online) && (
          <div class="empty">
            <p class="empty-title">Nenhuma coleção ainda.</p>
            <p class="empty-line">Coleções juntam cards por assunto. Crie uma do zero ou, na busca, guarde o que achou com “Salvar como coleção”.</p>
          </div>
        )
      )}
      {dialog && <SpaceDialog mode={{ kind: "create" }} onClose={() => setDialog(false)} />}
    </main>
  );
}

/** Pilha com os três primeiros cards (o da frente reto, os de trás inclinados), nome com o anel da cor e contagem. */
function SpaceTile(props: { space: Space; index: number; hint: string; onMove: (id: string, to: number) => void; onDropOn: (id: string, at: number, after: boolean) => void }) {
  const { space: sp, index, hint, onMove, onDropOn } = props;
  const cards = useSpaceCards(sp);
  const top = (cards ?? []).slice(0, 3);
  const [drop, setDrop] = useState<"before" | "after" | null>(null);
  const [moving, setMoving] = useState(false);
  const side = (e: DragEvent) => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    return e.clientX > r.left + r.width / 2 ? "after" : "before";
  };
  return (
    <li
      class="space-tile"
      data-id={sp.id}
      data-drop={drop ?? undefined}
      data-moving={moving || undefined}
      draggable
      onDragStart={(e) => {
        e.dataTransfer?.clearData();
        e.dataTransfer?.setData(SPACE_MIME, sp.id);
        e.dataTransfer!.effectAllowed = "move";
        setMoving(true);
      }}
      onDragEnd={() => setMoving(false)}
      onDragOver={(e) => {
        if (!e.dataTransfer?.types.includes(SPACE_MIME)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setDrop(side(e));
      }}
      onDragLeave={(e) => !(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node | null) && setDrop(null)}
      onDrop={(e) => {
        const id = e.dataTransfer?.getData(SPACE_MIME);
        setDrop(null);
        if (!id) return;
        e.preventDefault();
        if (id !== sp.id) onDropOn(id, index, side(e) === "after");
      }}
    >
      <a
        class="space-link"
        href={spacePath(sp.id)}
        onClick={navTo(spacePath(sp.id))}
        draggable={false}
        aria-describedby={hint}
        aria-keyshortcuts={MOVE_SHORTCUTS}
        onKeyDown={(e) => {
          if (!e.shiftKey || (e.key !== "ArrowLeft" && e.key !== "ArrowRight")) return;
          e.preventDefault();
          onMove(sp.id, index + (e.key === "ArrowRight" ? 1 : -1));
        }}
      >
        <span class="stack" data-n={top.length} aria-hidden="true">
          {top.length ? (
            [...top].reverse().map((c) => (
              <span class="stack-card" key={c.id}>
                <Thumb card={c} />
              </span>
            ))
          ) : (
            <span class="stack-empty">
              <span>Vazia</span>
            </span>
          )}
        </span>
        <h2 class="space-name">
          <Ring color={sp.color} />
          {sp.name}
        </h2>
        <span class="space-meta">
          {sp.kind === "smart" ? "Automática · " : ""}
          {cards ? cardsLabel(cards.length) : "…"}
        </span>
      </a>
    </li>
  );
}

const imageTypes = ACCEPTED_MIME.filter((m) => m.startsWith("image/")) as readonly string[];

/** Arquivos (imagens) ou um link soltos na coleção viram cards e entram nela. */
function addFiles(spaceId: string, dt: DataTransfer | FileList | null) {
  const files = dt instanceof FileList ? [...dt] : [...(dt?.files ?? [])];
  if (files.length) {
    const ok = files.filter((f) => imageTypes.includes(f.type));
    for (const f of ok) store.createImage(f).then((cid) => store.addToSpace(spaceId, cid)).catch(failed);
    if (ok.length < files.length) toast({ key: "only-images", text: "Por enquanto, só imagens podem ser guardadas como arquivo." });
    return true;
  }
  if (dt instanceof FileList || !dt) return false;
  const url = urlFromText(dt.getData("text/uri-list").split(/\r?\n/).find((l) => l && !l.startsWith("#")) ?? dt.getData("text/plain"));
  if (!url) return false;
  store.createLink(url).then((cid) => store.addToSpace(spaceId, cid)).catch(failed);
  return true;
}

function SpaceView({ id }: { id: string }) {
  const sp = store.spaces.value.find((x) => x.id === id);
  const cards = useSpaceCards(sp);
  const [dialog, setDialog] = useState<SpaceDialogMode | null>(null);
  const [menu, setMenu] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [said, setSaid] = useState("");
  const menuId = useId();
  const optBtn = useRef<HTMLButtonElement>(null);
  const optMenu = useRef<HTMLDivElement>(null);
  const file = useRef<HTMLInputElement>(null);
  const s = store.status.value;
  useEffect(() => {
    if (!menu) return;
    optMenu.current?.querySelector("button")?.focus(); // abre com o foco na primeira opção
    const away = (e: PointerEvent) => !(e.target as Element).closest(".space-opts") && setMenu(false);
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [menu]);

  if (!sp) {
    return (
      <main id="main" tabIndex={-1} class="page organize space-view">
        <header class="page-head org-head">
          <a class="round-back" href="/colecoes" onClick={backTo("/colecoes")} aria-label="Voltar para Coleções">
            <IconPrev />
          </a>
          <h1>Coleção</h1>
        </header>
        {(s.lastSyncAt !== null || !s.online) && (
          <div class="empty" role="status">
            <p class="empty-title">Esta coleção não existe mais.</p>
            <p class="empty-line">Ela pode ter sido apagada em outro aparelho. Os cards continuam em Tudo.</p>
          </div>
        )}
      </main>
    );
  }
  const manual = sp.kind === "manual";
  /** Nome vazio volta ao anterior; repetido também (com o aviso), para o campo não mostrar um nome que não ficou. */
  const rename = (el: HTMLInputElement) => {
    const name = el.value.trim();
    if (!name || name === sp.name) return void (el.value = sp.name);
    if (nameTaken(store.spaces.value, name, sp.id)) {
      el.value = sp.name;
      return toast({ key: "space-taken", text: "Já existe uma coleção com esse nome." });
    }
    store.updateSpace(sp.id, { name }).catch(failed);
  };
  // Na automática, tirar grava uma exclusão e nenhuma operação a apaga: desfazer com addToSpace viraria inclusão fixa
  // (o card ficaria mesmo sem combinar com a busca). Só a manual oferece Desfazer.
  const remove = (c: Card) => {
    const pos = store.spaceCards.value.get(sp.id)?.find((r) => r.cardId === c.id)?.position ?? undefined;
    store.removeFromSpace(sp.id, c.id).then(
      () =>
        toast({
          key: `out:${sp.id}:${c.id}`,
          text: manual ? `Tirado de “${sp.name}”. O card continua em Tudo.` : `Tirado de “${sp.name}”. Não volta pela busca; o card continua em Tudo.`,
          action: manual ? { label: "Desfazer", run: () => void store.addToSpace(sp.id, c.id, pos).catch(failed) } : undefined,
        }),
      failed,
    );
  };
  const reorder = (cardId: string, to: number) => {
    const order = (cards ?? []).map((c) => c.id);
    const pos = new Map((store.spaceCards.value.get(sp.id) ?? []).map((r) => [r.cardId, r.position]));
    const p = moveTo(order, pos, cardId, to);
    if (p === null) return;
    store.reorderSpaceCard(sp.id, cardId, p).catch(failed);
    setSaid(`Posição ${Math.min(to, order.length - 1) + 1} de ${order.length}.`);
    requestAnimationFrame(() => document.querySelector<HTMLElement>(`.space-item a[href="#${CSS.escape(encodeURIComponent(cardId))}"]`)?.focus());
  };

  /** Soltou `moving` antes/depois do card no índice `at`: o índice final conta que ele sai do lugar antigo. */
  const dropOn = (moving: string, at: number, after: boolean) => {
    const from = (cards ?? []).findIndex((x) => x.id === moving);
    const to = at + (after ? 1 : 0);
    reorder(moving, from >= 0 && from < to ? to - 1 : to);
  };

  return (
    <main
      id="main"
      tabIndex={-1}
      class="page organize space-view"
      onDragOver={(e) => e.dataTransfer?.types.includes("Files") && e.preventDefault()}
      onDrop={(e) => {
        if (e.defaultPrevented || !e.dataTransfer?.types.includes("Files")) return;
        e.preventDefault();
        addFiles(sp.id, e.dataTransfer);
      }}
    >
      <header class="page-head org-head space-head">
        {/* O app instalado não tem o voltar do navegador. No celular: voltar, anel e nome no meio, opções. */}
        <a class="round-back" href="/colecoes" onClick={backTo("/colecoes")} aria-label="Voltar para Coleções">
          <IconPrev />
        </a>
        <div class="space-name-row">
          <button type="button" class="space-ring-btn" style={ringOf(sp.color)} aria-label="Mudar a cor da coleção" data-tip="Mudar a cor" onClick={() => setDialog({ kind: "color", space: sp })} />
          <h1 class="space-title">
            {/* O campo cresce com o nome (o ::after copia o texto), para o anel e o nome ficarem juntos no meio. */}
            <span class="space-title-fit" data-value={sp.name}>
              <input
                key={sp.name}
                class="space-title-input"
                aria-label="Nome da coleção"
                defaultValue={sp.name}
                maxLength={80}
                size={1} // a largura vem do CSS (cheia no desktop; do tamanho do nome no celular)
                onInput={(e) => ((e.currentTarget.parentElement as HTMLElement).dataset.value = e.currentTarget.value)}
                onBlur={(e) => {
                  const el = e.currentTarget;
                  rename(el); // pode voltar ao nome anterior
                  (el.parentElement as HTMLElement).dataset.value = el.value;
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.currentTarget.blur();
                  if (e.key === "Escape") (e.currentTarget.value = sp.name), e.currentTarget.blur();
                }}
              />
            </span>
          </h1>
        </div>
        <div class="space-opts">
          <button type="button" class="round-opt" ref={optBtn} aria-label="Opções da coleção" aria-expanded={menu} aria-controls={menu ? menuId : undefined} onClick={() => setMenu(!menu)}>
            <IconChevronDown />
          </button>
          {menu && (
            <div
              id={menuId}
              ref={optMenu}
              class="opts-pop"
              onKeyDown={(e) => {
                if (e.key !== "Escape") return;
                e.preventDefault();
                setMenu(false);
                optBtn.current?.focus();
              }}
            >
              <button type="button" class="menu-item" onClick={() => (setMenu(false), setDialog({ kind: "color", space: sp }))}>
                Mudar a cor
              </button>
              <button type="button" class="menu-item" onClick={() => (setMenu(false), document.querySelector<HTMLInputElement>(".space-title-input")?.select())}>
                Renomear
              </button>
              <button type="button" class="menu-item danger" onClick={() => (setMenu(false), setConfirm(true))}>
                Apagar coleção
              </button>
            </div>
          )}
        </div>
      </header>
      <p class="space-sub">
        {sp.kind === "smart" ? (
          <>
            Automática: cards que combinam com <span class="chip">{sp.query}</span> entram sozinhos.
          </>
        ) : (
          <span class="drag-hint">Arraste para mudar a ordem.</span>
        )}{" "}
        {cards && <span class="space-count">{cardsLabel(cards.length)}</span>}
      </p>
      <QueryVersionNote space={sp} />
      {cards && cards.length > 0 && (
        <Grid
          cards={cards}
          lead={false}
          resetKey={sp.id}
          density={density.value}
          render={(c) => <SpaceItem key={c.id} card={c} space={sp} index={cards.indexOf(c)} onRemove={remove} onMove={reorder} onDropOn={dropOn} />}
        />
      )}
      {cards && cards.length === 0 && (
        <div class="empty space-empty">
          <p class="empty-title">{manual ? "Esta coleção está vazia." : "Nada combina com esta busca ainda."}</p>
          <p class="empty-line">
            {manual
              ? "Abra um card e use o anel “Adicionar a uma coleção”, arraste cards até o nome dela em Tudo ou selecione vários de uma vez."
              : "Cards novos que combinarem entram aqui sozinhos. Também dá para incluir um card pelo anel no detalhe dele."}
          </p>
          <button type="button" class="drop-circle" aria-label="Escolher imagens para esta coleção" onClick={() => file.current?.click()}>
            <IconUp />
          </button>
          <p class="drop-note" aria-hidden="true">
            Ou solte imagens aqui
          </p>
          <input
            ref={file}
            type="file"
            accept={imageTypes.join(",")}
            multiple
            hidden
            onChange={(e) => {
              addFiles(sp.id, e.currentTarget.files);
              e.currentTarget.value = "";
            }}
          />
        </div>
      )}
      <p class="sr-only" aria-live="polite">
        {said}
      </p>
      {dialog && <SpaceDialog mode={dialog} onClose={() => setDialog(null)} />}
      {confirm && (
        <Confirm
          title={`Apagar “${sp.name}”?`}
          line="A coleção some deste e dos outros aparelhos. Os cards continuam guardados em Tudo."
          action="Apagar coleção"
          danger
          onConfirm={async () => {
            await store.deleteSpace(sp.id);
            up("/colecoes"); // a coleção apagada não fica no histórico atrás de Coleções
            toast({ key: `del-space:${sp.id}`, text: `Coleção “${sp.name}” apagada. Os cards continuam em Tudo.` });
          }}
          onClose={() => setConfirm(false)}
        />
      )}
    </main>
  );
}

/** Card dentro da coleção: abre o detalhe, sai da coleção pelo × e, na manual, muda de lugar arrastando ou com Shift+setas. */
function SpaceItem(props: { card: Card; space: Space; index: number; onRemove: (c: Card) => void; onMove: (id: string, to: number) => void; onDropOn: (id: string, at: number, after: boolean) => void }) {
  const { card: c, space: sp, index, onRemove, onMove, onDropOn } = props;
  const [drop, setDrop] = useState<"before" | "after" | null>(null);
  const manual = sp.kind === "manual";
  const kind = KIND_LABEL[c.kind];
  const label = cardText(c).slice(0, 120);
  const name = label === kind ? kind : `${kind}: ${label}`;
  const side = (e: DragEvent) => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    return e.clientY > r.top + r.height / 2 ? "after" : "before";
  };
  return (
    <article
      class="card space-item"
      data-kind={c.kind}
      data-cap={captionOf(c) ? "" : undefined}
      data-drop={drop ?? undefined}
      draggable={manual}
      onDragStart={(e) => {
        e.dataTransfer?.clearData();
        e.dataTransfer?.setData("application/x-faig-move", c.id);
        e.dataTransfer!.effectAllowed = "move";
      }}
      onDragOver={(e) => {
        if (!manual || !e.dataTransfer?.types.includes("application/x-faig-move")) return;
        e.preventDefault();
        setDrop(side(e));
      }}
      onDragLeave={() => setDrop(null)}
      onDrop={(e) => {
        const moving = e.dataTransfer?.getData("application/x-faig-move");
        setDrop(null);
        if (!manual || !moving) return;
        e.preventDefault();
        if (moving !== c.id) onDropOn(moving, index, side(e) === "after");
      }}
    >
      <a
        class="card-open"
        href={`#${encodeURIComponent(c.id)}`}
        aria-label={name}
        aria-keyshortcuts={manual ? "Shift+ArrowLeft Shift+ArrowRight" : undefined}
        draggable={false}
        onKeyDown={(e) => {
          if (!manual || !e.shiftKey || (e.key !== "ArrowLeft" && e.key !== "ArrowRight")) return;
          e.preventDefault();
          onMove(c.id, index + (e.key === "ArrowRight" ? 1 : -1));
        }}
      >
        <Frame card={c} />
        <Caption card={c} />
      </a>
      <GridIngest card={c} />
      <ImageRetry card={c} name={name} />
      <button type="button" class="space-x" aria-label={`Tirar da coleção: ${name}`} data-tip="Tirar da coleção" onClick={() => onRemove(c)}>
        <IconClose />
      </button>
    </article>
  );
}
